import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "./pageList";
import { useFormRender } from "../hooks/useFormRender";
import { useTranslation } from "react-i18next";
import {
  Button,
  TextInput,
  Switch,
  Select,
  Textarea,
  ActionIcon,
  NumberInput,
  UnstyledButton,
  Group,
  Text,
} from "@mantine/core";
import {
  IconCircle,
  IconCirclePlus,
  IconExclamationMark,
  IconSettings,
} from "@tabler/icons";

import { DATA_BANK_WITH_CODE } from "../data/options";
import { DATA_CLIENT_PAYMENT_METHOD } from "../data/options";
import { DatePicker } from "@mantine/dates";
import FileList from "./fileList";

import { useServerApi } from "../hooks/userServerApi";
import { showNotification } from "@mantine/notifications";
import _ from "lodash";
import RemoteSelect from "../components/remoteSelect";
import RemoteSelect2 from "../components/remoteSelect2";
import { DATA_ORDER_TYPE, DATA_COUNTRY } from "../data/options";
import Chips from "./chips";

import FormConfirmation from "./formConfirmation";
import RemarkList from "./remarkList";
import ReactJson from "react-json-view";
import { useFormatter } from "../hooks/useFomatter";
import DebitNotePaymentMatchingList from "./debitNotePaymentMatchingList";
import { useAuthUser } from "react-auth-kit";
import { useForceUpdate } from "@mantine/hooks";

const AddClientPaymentButton = ({ mainForm, onAdded }) => {
  const [api] = useServerApi();
  const apiEntity = "clientPayment";
  const [formatter] = useFormatter();
  const initialValues = {};
  const auth = useAuthUser();
  const clientId = mainForm?.values?.client?._id;
  const dn = mainForm?.values;
  const [defaultBankAccount, setDefaultBankAccount] = useState(null);
  const formActionRef = useRef(null);
  const handleReceiptAutoSaveRef = useRef(null);

  const formSchema = useMemo(
    () => ({
      name: "AddClientPaymentSchema",
      title: "Add Payment",
      description: "Please follow the step to create a New Client Payment",

      display: {
        mode: ENUM_FORM_DISPLAY.MODAL,
        size: "1000px", //xs, sm, xl  ....100%
      },

      apiEntity,
      initialValues: {
        bankAccount: defaultBankAccount?._id,
      },

      validate: {
        amount: (value, data) => {
          const totalAssignedAmount = +_.sumBy(
            data.debitNoteAssignments,
            "paymentAmount"
          ).toFixed(2);
          // console.log("amount", value, totalAssignedAmount);

          // if (value !== totalAssignedAmount) {
          //   return `Payment Amount (${value}) must be equal to the total assigned amount (${totalAssignedAmount})`;
          // }

          return null;
        },
        // bankAccount: (value) => {
        //   if (!value) return "Bank Account is required";
        //   return null;
        // },
        date: (value) => {
          if (!value) return "Payment Date is required";
          return null;
        },
        // files: (value) => {
        //   if (!value) return "Receipt File is required";
        //   return null;
        // },
      },
      showSaveBar: true,

      layout: {
        containers: [
          {
            key: "t1",
            parent: null,
            type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
            props: {
              variant: "default", //default | pills | outline
              defaultValue: "Payment Info",
            },
            tab: [
              {
                key: "t1",
                label: "Payment Info",
                //icon: IconSettings,
              },
              {
                key: "tab-multiple",
                label: "Multiple Debit Notes",
                // icon: IconSettings,
                showTab: (values, accessRight) => dn.type !== "CREDIT",
              },
            ],
          },
        ],

        fields: [
          //Payment Date
          {
            name: "date",
            component: DatePicker,
            parent: "t1",
            props: {
              label: "Payment Date",
              required: true,
              clearable: true,
              valueFormat: "YYYY-MM-DD",
              allowFreeInput: true,
            },
          },

          //Payment Amount
          {
            name: "amount",
            component: NumberInput,
            parent: "t1",
            props: {
              label: "Payment Amount",
              //parse as money
              parser: formatter.currencyParser,
              formatter: formatter.currencyFormat,
              precision: 2,
            },
          },

          //adjusment type
          {
            name: "adjustmentType",
            component: Select,
            parent: "t1",
            visibleOnly: (values, accessRight) => dn.type !== "CREDIT",
            props: {
              label: "Adjustment Type",
              data: [
                "none",
                "Bank Charge",
                "Payment Amount Adjustment",
                "Commission",
                "A/P - Referral fee",
                "Temporary Receipt for Pending Policy",
                "Other",
              ],
              searchable: true,
              clearable: true,
              defaultValue: "none",
            },
          },

          //adjustment amount if type is not none
          {
            name: "adjustmentAmount",
            component: NumberInput,
            parent: "t1",
            visibleOnly: (values, accessRight) => dn.type !== "CREDIT",
            props: {
              label: "Adjustment Amount",
              parser: formatter.currencyParser,
              formatter: formatter.currencyFormat,
              precision: 2,
              defaultValue: 0,
            },
          },

          //Payment Method
          {
            name: "paymentMethod",
            component: Select,
            parent: "t1",
            props: {
              label: "Payment Method",
              data: DATA_CLIENT_PAYMENT_METHOD,
              searchable: true,
              clearable: true,
            },
          },
          //Cheque Number
          {
            name: "chequeNumber",
            component: TextInput,
            parent: "t1",
            visibleOnly: (values, accessRight) =>
              values.paymentMethod === "Cheque",
            props: {
              label: "Cheque Number",
            },
          },

          //Direct to Provider
          {
            name: "directToProvider",
            parent: "t1",
            component: Switch,
            visibleOnly: (values, accessRight) => dn.type !== "CREDIT",
            props: {
              type: "checkbox",
              size: "md",
              label: "Direct to Provider ",
              mt: "md",
            },
          },

          //Bank Account
          {
            name: "bankAccount",
            component: RemoteSelect2,
            parent: "t1",
            visibleOnly: (values, accessRight) =>
              values.paymentMethod !== "CLIENT_CREDIT" &&
              !values.directToProvider,
            props: {
              label: "Bank Account",
              apiEntity: "bankAccount",
              required: true,
              placeholder: "Bank Account",
              valueField: "_id",
              labelField: "name",

              // allowClear: true,
              // clearable: true,
              labelRender: (data) => {
                //Get bank name from bankAccount.bank
                return ` ${data.shortName ?? ""} : ${
                  data.accountNumber ?? ""
                } `;
              },
              multiple: false,
              searchFields: ["name", "code", "cname"],
              clearable: true,
              sort: { by: "bank", order: "asc" },
            },
          },

          //Receipt File
          {
            name: "files",
            component: FileList,
            parent: "t1",
            visibleOnly: (values, accessRight) =>
              values.paymentMethod !== "CLIENT_CREDIT",
            props: {
              title: "Receipt File",
              tableMarginBottom: 10,
              showNoFilesMessage: false,
              defaultDisplayMode: 1,
              onAutoSave: (updatedFiles, form) =>
                handleReceiptAutoSaveRef.current?.(updatedFiles, form),
            },
          },
          {
            name: "remark",
            parent: "t1",
            component: Textarea,
            props: {
              label: "Remark",
            },
          },
          // {
          //   name: "isMultiple",
          //   parent: "section-debit-note",
          //   component: Switch,
          //   props: {
          //     label: "Pay to multiple debit notes",
          //   },
          // },
          {
            name: "debitNotes",
            parent: "tab-multiple",
            component: DebitNotePaymentMatchingList,
            props: {
              label: "Debit Notes",
              clientId,
            },
          },
        ],
      },
    }),
    [
      defaultBankAccount,
      formatter.currencyParser,
      formatter.currencyFormat,
      clientId,
    ]
  );

  const forceUpdate = useForceUpdate();

  const fetchDefaultBankAccount = async () => {
    const result = await api.search({
      apiEntity: "bankAccount",
      searchQuery: {
        isDefault: true,
      },
      pageSize: 1,
      select: "name accountNumber shortName _id",
    });
    console.log("result", result);

    if (!result || !result.docs || result.docs.length === 0) return;

    console.log("bankAccount", result.docs[0]);
    setDefaultBankAccount(result.docs[0]._id);
  };

  useEffect(() => {
    fetchDefaultBankAccount();
  }, []);

  const addClientPayment = useCallback(
    async (values, { closeForm = true } = {}) => {
      if (!values.date) {
        window.alert("Payment Date is required");
        return;
      }
      if (
        !values.bankAccount &&
        !values.directToProvider &&
        values.paymentMethod !== "CLIENT_CREDIT"
      ) {
        window.alert("Bank Account is required");
        return;
      }
      const totalAssignedAmount = +_.sumBy(
        values.debitNoteAssignments,
        "paymentAmount",
      ).toFixed(2);

      if (Math.abs(values.amount - totalAssignedAmount) > 0.01) {
        window.alert(
          `Payment Amount (${values.amount}) must be equal to the total assigned amount (${totalAssignedAmount})`,
        );
        return;
      }

      const data = await api.ClientPayment.add({
        payment: values,
        debitNote: mainForm?.values._id,
      });

      if (closeForm) formActionRef.current?.close();
      if (onAdded) onAdded(data);

      const cp = data?.clientPayment ?? data ?? {};
      const paymentCode = cp.code ?? "";
      const paymentAmount =
        cp.amount != null
          ? `HKD ${Number(cp.amount).toLocaleString("en-HK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : "";
      const paymentMethod = cp.paymentMethod ?? "";
      const paymentDate = cp.date
        ? new Date(cp.date).toLocaleDateString("en-HK", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "";
      const paymentRemark = cp.remark ?? "";

      const paymentChanges = [
        paymentCode && { fieldLabel: "Code", oldValue: "", newValue: paymentCode },
        paymentAmount && { fieldLabel: "Amount", oldValue: "", newValue: paymentAmount },
        paymentMethod && { fieldLabel: "Method", oldValue: "", newValue: paymentMethod },
        paymentDate && { fieldLabel: "Date", oldValue: "", newValue: paymentDate },
        paymentRemark && { fieldLabel: "Remark", oldValue: "", newValue: paymentRemark },
      ].filter(Boolean);

      api.addActivityLog({
        entityId: mainForm?.values._id,
        title: `Payment Added${paymentCode ? ` [${paymentCode}]` : ""}`,
        by: auth().id,
        description: [paymentAmount, paymentMethod, paymentDate]
          .filter(Boolean)
          .join(" | "),
        changes: paymentChanges,
      });

      mainForm.reload();
      showNotification({
        title: "Payment Added",
        message: "Payment added successfully",
      });
      return data;
    },
    [api, auth, mainForm, onAdded],
  );

  handleReceiptAutoSaveRef.current = async (updatedFiles, form) => {
    if (!form || form.values?._id) return;

    const values = { ...form.values, files: updatedFiles };
    if (!values.date) return;
    if (
      !values.bankAccount &&
      !values.directToProvider &&
      values.paymentMethod !== "CLIENT_CREDIT"
    ) {
      return;
    }

    const totalAssignedAmount = +_.sumBy(
      values.debitNoteAssignments,
      "paymentAmount",
    ).toFixed(2);
    if (Math.abs(values.amount - totalAssignedAmount) > 0.01) return;

    try {
      await addClientPayment(values, { closeForm: true });
    } catch (error) {
      showNotification({
        color: "red",
        icon: <IconExclamationMark size={18} />,
        title: "Service add error",
        message: error.msg ?? error.message,
      });
    }
  };

  const onSubmit = async ({ values }) => {
    try {
      await addClientPayment(values, { closeForm: true });
    } catch (error) {
      showNotification({
        color: "red",
        icon: <IconExclamationMark size={18} />,
        title: "Service add error",
        message: error.msg ?? error.message,
      });
    }
  };

  const [renderForm, formAction, formStatus, formSetting] = useFormRender(
    formSchema,
    null,
    onSubmit,
  );
  formActionRef.current = formAction;

  // Update form when defaultBankAccount is loaded
  useEffect(() => {
    if (defaultBankAccount && formAction && formStatus.isOpen) {
      formAction.setFieldValue("bankAccount", defaultBankAccount);
    }
  }, [defaultBankAccount, formStatus.isOpen]);

  const handleActionClick = () => {
    if (!mainForm || !mainForm.values) return;

    if (
      // mainForm.values.paymentStatus === "PAID" ||
      mainForm.values.paymentStatus === "SETTLED"
    ) {
      showNotification({
        color: "red",
        icon: <IconExclamationMark size={18} />,
        title: "Error",
        message: `Payment for ${mainForm.values.code} is already ${mainForm.values.paymentStatus}, cannot add new payment`,
      });
      return;
    }

    const id = mainForm?.values._id;
    if (!id) return;

    const v = {
      date: null,
      amount:
        mainForm.values.paymentStatus === "WAIT"
          ? mainForm.values.clientPayable
          : 0,
      bankAccount: defaultBankAccount?._id,
      remark: "",
      files: [],
      isMultiple: false,
      debitNoteAssignments: [
        {
          debitNote: mainForm.values,
          paymentAmount: mainForm.values.clientPayable,
        },
      ],
    };

    formAction.open({ mode: "add", data: v });
  };

  const { t } = useTranslation();
  return (
    <>
      <UnstyledButton onClick={() => handleActionClick()}>
        <Group spacing={"xs"}>
          <ActionIcon>
            <IconCirclePlus size={"1rem"} />
          </ActionIcon>
          <Text size={"xs"}>{t("Add Payment")}</Text>
        </Group>
      </UnstyledButton>
      {renderForm()}
    </>
  );
};

export default AddClientPaymentButton;
